import styled from "@emotion/styled";
import React, { FC } from "react";
import translate from "src/lib/translate";

const Layout = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 1rem;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.125rem;
`;

const Value = styled.div`
  font-size: 1rem;
  font-weight: 500;
  line-height: 140%;
  white-space: nowrap;
`;

const Label = styled.div`
  font-weight: 400;
  white-space: nowrap;
`;

const Average = styled.em`
  display: block;
  margin-top: 0.125rem;
  font-size: 0.6875rem;
`;

export interface BodyCompositionTooltipProps {
  /** Formatted muscle mass value (without unit). */
  muscleValue: string;
  /** Formatted fat mass value (without unit). May be absent if unpaired. */
  fatValue?: string;
  unit: string;
  /** Whether the point is an averaged value over the interval. */
  isAverage?: boolean;
}

/**
 * Body composition tooltip: renders muscle mass and fat mass side by side, each
 * with its own value+unit and a descriptive label underneath, instead of the
 * hard-to-parse "muscle/fat unit" single line used by other charts.
 */
const BodyCompositionTooltip: FC<BodyCompositionTooltipProps> = ({
  muscleValue,
  fatValue,
  unit,
  isAverage = false
}) => (
  <div>
    <Layout>
      <Column>
        <Value>
          {muscleValue} {unit}
        </Value>
        <Label className="as-caption">
          {translate("bodyComposition.tooltip.muscleMass")}
        </Label>
      </Column>
      {fatValue !== undefined && (
        <Column>
          <Value>
            {fatValue} {unit}
          </Value>
          <Label className="as-caption">
            {translate("bodyComposition.tooltip.fatMass")}
          </Label>
        </Column>
      )}
    </Layout>
    {isAverage && <Average>{translate("chart.average")}</Average>}
  </div>
);

export default BodyCompositionTooltip;
